/** kware start edit - Create change password modal for staff users
 * This modal allows admins to directly change staff user passwords without sending reset links
 * Reuses the same password input logic from EditPasswordSection component
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
  Modal,
  Button,
  TextField,
  Row,
  Col,
  IconButton,
  ModalFooter,
  MessageBanner,
} from '@folio/stripes/components';

const ChangePasswordModal = ({
  isOpen,
  onClose,
  onSubmit,
  userName,
  isLoading,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!password || !confirmPassword) {
      setError('ui-users.errors.missingRequiredField');
      return;
    }

    if (password !== confirmPassword) {
      setError('ui-users.errors.passwordMismatch');
      return;
    }

    onSubmit(password);
  };

  const handleClose = () => {
    setPassword('');
    setConfirmPassword('');
    setError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const footer = (
    <ModalFooter>
      <Button
        buttonStyle="primary"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        <FormattedMessage id="ui-users.saveAndClose" />
      </Button>
      <Button
        onClick={handleClose}
        disabled={isLoading}
      >
        <FormattedMessage id="ui-users.cancel" />
      </Button>
    </ModalFooter>
  );

  return (
    <Modal
      open={isOpen}
      label={<FormattedMessage id="ui-users.extended.changePasswordModal.label" />}
      onClose={handleClose}
      footer={footer}
      size="medium"
    >
      <MessageBanner type="warning">
        <FormattedMessage id="ui-users.extended.changePasswordModal.warning" />
      </MessageBanner>
      <br />
      <p>
        <FormattedMessage
          id="ui-users.extended.changePasswordModal.description"
          values={{ name: <strong>{userName}</strong> }}
        />
      </p>
      <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <strong><FormattedMessage id="ui-users.extended.changePasswordModal.bestPractices" /></strong>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          <li><FormattedMessage id="ui-users.extended.changePasswordModal.practice1" /></li>
          <li><FormattedMessage id="ui-users.extended.changePasswordModal.practice2" /></li>
          <li><FormattedMessage id="ui-users.extended.changePasswordModal.practice3" /></li>
          <li><FormattedMessage id="ui-users.extended.changePasswordModal.practice4" /></li>
        </ul>
      </div>
      <Row>
        <Col xs={12}>
          <TextField
            label={<FormattedMessage id="ui-users.extended.changePasswordModal.newPassword" />}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            required
            fullWidth
            disabled={isLoading}
            endControl={
              <IconButton
                icon={showPassword ? 'eye-open' : 'eye-closed'}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              />
            }
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <TextField
            label={<FormattedMessage id="ui-users.extended.changePasswordModal.confirmPassword" />}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError('');
            }}
            required
            fullWidth
            disabled={isLoading}
            error={error ? <FormattedMessage id={error} /> : null}
            endControl={
              <IconButton
                icon={showConfirmPassword ? 'eye-open' : 'eye-closed'}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              />
            }
          />
        </Col>
      </Row>
    </Modal>
  );
};

ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  userName: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

export default ChangePasswordModal;
/** kware end edit */
